import { Component, type ErrorInfo, type ReactNode } from "react";

import { statusOf } from "./queryClient.js";

/**
 * `P0-UIK-013`: the error boundary that feeds `ErrorScreen`.
 *
 * **Danger sentence.** React's default for an uncaught render error is to
 * unmount the whole tree, and a blank white page is the single worst thing this
 * product can show: it tells a client in Bamako nothing, gives them nothing to
 * do, and gives whoever they telephone nothing to go on. 4.6.6's rule is that a
 * screen is never a dead end, and a blank page is the purest dead end there is.
 *
 * **What it renders is not its own words.** `ErrorScreen` requires the SERVER's
 * French sentence and refuses an empty one, because 4.5.5 writes that sentence
 * beside the guard that produced it. So this boundary carries a refusal's
 * status and message through untouched, and falls back to the app's catalogue
 * sentence only for a genuine crash , which has no server sentence precisely
 * because no server was involved.
 *
 * **It holds no French**, per 6.5.1 and `hakili/no-french-outside-catalogue`:
 * the app passes `crashMessage`, so the words stay correctable in one place.
 */

export interface ShellErrorBoundaryFallback {
  /** Chooses the heading. `null` means a crash rather than a refusal. */
  readonly code: 401 | 403 | 404 | null;
  /** The sentence to show: the server's whenever there is one. */
  readonly message: string;
}

export interface ShellErrorBoundaryProps {
  /** Renders the fallback. The app supplies the chrome, because this package has none. */
  readonly render: (fallback: ShellErrorBoundaryFallback) => ReactNode;
  /** The app's catalogue sentence for a crash that carries no server sentence. */
  readonly crashMessage: string;
  /** Reported to the app's telemetry. The shell does not decide what happens to it. */
  readonly onError?: (failure: unknown, info: ErrorInfo) => void;
  readonly children: ReactNode;
}

interface ShellErrorBoundaryState {
  readonly failure: unknown;
  readonly caught: boolean;
}

/** The message a rejection carries, or `""` when it carries none worth showing. */
function sentenceOf(failure: unknown): string {
  if (typeof failure !== "object" || failure === null) return "";
  const message = (failure as { message?: unknown }).message;
  return typeof message === "string" ? message.trim() : "";
}

export class ShellErrorBoundary extends Component<ShellErrorBoundaryProps, ShellErrorBoundaryState> {
  public override state: ShellErrorBoundaryState = { failure: null, caught: false };

  public static getDerivedStateFromError(failure: unknown): ShellErrorBoundaryState {
    return { failure, caught: true };
  }

  public override componentDidCatch(failure: unknown, info: ErrorInfo): void {
    this.props.onError?.(failure, info);
  }

  public override render(): ReactNode {
    if (!this.state.caught) return this.props.children;

    const status = statusOf(this.state.failure);
    const sentence = sentenceOf(this.state.failure);
    const isRefusal = status === 401 || status === 403 || status === 404;

    return this.props.render({
      code: isRefusal ? status : null,
      // A refusal carrying no sentence still gets one, because `ErrorScreen`
      // refuses to render a heading over blank space , and a boundary that
      // threw inside its own fallback would produce exactly the blank page it
      // exists to prevent.
      message: isRefusal && sentence !== "" ? sentence : this.props.crashMessage,
    });
  }
}
